import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    IconButton,
    Grid,
    Chip,
    Button,
    Divider,
    useMediaQuery,
    useTheme,
    Tooltip,
} from '@mui/material';
import {
    IconX,
    IconMapPin,
    IconScale,
    IconCurrencyRupee,
    IconLeaf,
    IconTruck,
    IconCalendar,
    IconChevronLeft,
    IconChevronRight,
    IconZoomIn,
    IconZoomOut,
    IconRefresh,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { fonts, colors } from '../ColorFont/color';
import Carousel from 'react-material-ui-carousel';
import { url } from '../ConstUrl/Url';

const CropDetailsModal = ({ open, onClose, crop, userData, onGetContact }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Image Viewer States
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerImage, setViewerImage] = useState('');
    const [zoom, setZoom] = useState(1);

    const handleImageClick = (imageUrl) => {
        setViewerImage(imageUrl);
        setViewerOpen(true);
        setZoom(1);
    };

    if (!crop) return null;

    const photos = Array.isArray(crop.photos) ? crop.photos : [];
    const isOrganic = crop.isOrganic || false;
    const isNegotiable = crop.isNegotiable || true;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : '24px',
                    margin: isMobile ? 0 : 2,
                    maxHeight: isMobile ? '100%' : '90vh',
                    overflow: 'hidden',
                    backgroundColor: '#FFFFFF',
                },
            }}
            fullScreen={isMobile}
        >
            {/* Close Button */}
            <IconButton
                onClick={onClose}
                sx={{
                    position: 'absolute',
                    right: 16,
                    top: 16,
                    zIndex: 10,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(4px)',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                }}
            >
                <IconX size={24} />
            </IconButton>

            <DialogContent sx={{ p: 0, overflowY: 'auto' }}>
                <Grid container>
                    {/* Left Side: Image Gallery */}
                    <Grid item xs={12} md={6}>
                        <Box sx={{ position: 'relative', height: { xs: '300px', md: '100%' }, backgroundColor: '#F4F6F8' }}>
                            {photos.length > 0 ? (
                                <Carousel
                                    autoPlay={false}
                                    animation="slide"
                                    indicators={photos.length > 1}
                                    navButtonsAlwaysVisible={photos.length > 1}
                                    sx={{ height: '100%' }}
                                >
                                    {photos.map((photo, index) => (
                                        <Box
                                            key={index}
                                            component="img"
                                            onClick={() => handleImageClick(`${url}/${photo}`)}
                                            sx={{
                                                width: '100%',
                                                height: { xs: '300px', md: '100%' },
                                                objectFit: 'cover',
                                                cursor: 'pointer',
                                            }}
                                        />
                                    ))}
                                </Carousel>
                            ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#919EAB' }}>
                                    <IconLeaf size={64} stroke={1.5} />
                                </Box>
                            )}

                            {isOrganic && (
                                <Chip
                                    label="Organic"
                                    icon={<IconLeaf size={16} color="#FFFFFF" />}
                                    sx={{
                                        position: 'absolute',
                                        bottom: 16,
                                        left: 16,
                                        backgroundColor: colors.primary,
                                        color: '#FFFFFF',
                                        fontWeight: 700,
                                        zIndex: 5,
                                    }}
                                />
                            )}
                        </Box>
                    </Grid>

                    {/* Right Side: Content */}
                    <Grid item xs={12} md={6}>
                        <Box sx={{ p: { xs: 3, md: 4 } }}>
                            <Typography sx={{
                                fontFamily: fonts.poppins,
                                fontSize: '14px',
                                color: colors.primary,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '1px',
                                mb: 1
                            }}>
                                {t(`categories.${crop.category?.[0] || 'Other'}`, { defaultValue: crop.category?.[0] || 'Produce' })}
                            </Typography>

                            <Typography variant="h4" sx={{
                                fontFamily: fonts.poppins,
                                fontWeight: 800,
                                color: '#1C252E',
                                mb: 2,
                                lineHeight: 1.2
                            }}>
                                {t(`crops.${(crop.subCategory || crop.name || '').replace('crops.', '')}`, { defaultValue: (crop.subCategory || crop.name || '').replace('crops.', '') })}
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                <Typography sx={{
                                    fontFamily: fonts.poppins,
                                    fontSize: '24px',
                                    fontWeight: 700,
                                    color: colors.primary_dark
                                }}>
                                    ₹{crop.amount}
                                </Typography>
                                <Chip
                                    label={isNegotiable ? "Negotiable" : "Fixed Price"}
                                    variant="outlined"
                                    size="small"
                                    sx={{ borderRadius: '6px', fontSize: '12px' }}
                                />
                            </Box>

                            <Divider sx={{ my: 3 }} />

                            <Grid container spacing={3}>
                                <Grid item xs={6}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box sx={{ p: 1, borderRadius: '12px', bgcolor: colors.primary_rgba_10, color: colors.primary }}>
                                            <IconScale size={20} />
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#919EAB', display: 'block' }}>Quantity</Typography>
                                            <Typography sx={{ fontWeight: 600 }}>{crop.area} {crop.areaUnit}</Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box sx={{ p: 1, borderRadius: '12px', bgcolor: 'rgba(255, 86, 48, 0.1)', color: '#FF5630' }}>
                                            <IconMapPin size={20} />
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#919EAB', display: 'block' }}>Location</Typography>
                                            <Typography sx={{ fontWeight: 600 }}>{crop.taluka}, {crop.state}</Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box sx={{ p: 1, borderRadius: '12px', bgcolor: 'rgba(0, 184, 217, 0.1)', color: '#00B8D9' }}>
                                            <IconTruck size={20} />
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#919EAB', display: 'block' }}>Delivery Availability</Typography>
                                            <Typography sx={{ fontWeight: 600 }}>{crop.deliveryDetails || 'Available for delivery'}</Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>

                            <Box sx={{ mt: 4 }}>
                                <Typography variant="h6" sx={{ fontFamily: fonts.poppins, fontWeight: 700, mb: 1 }}>Description</Typography>
                                <Typography sx={{ color: '#637381', lineHeight: 1.6 }}>
                                    {crop.description || "Fresh and high-quality produce direct from the farm. Carefully harvested to ensure maximum freshness and nutritional value."}
                                </Typography>
                            </Box>

                            <Box sx={{ mt: 5 }}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={() => onGetContact(crop)}
                                    sx={{
                                        height: '56px',
                                        borderRadius: '16px',
                                        fontFamily: fonts.poppins,
                                        fontWeight: 700,
                                        fontSize: '16px',
                                        backgroundColor: colors.primary,
                                        '&:hover': { backgroundColor: colors.primary_dark },
                                        boxShadow: '0 8px 16px colors.primary_rgba_24'
                                    }}
                                >
                                    Get Contact Details
                                </Button>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>

            {/* Image Viewer Lightbox */}
            <Dialog
                open={viewerOpen}
                onClose={() => setViewerOpen(false)}
                maxWidth="md"
                PaperProps={{
                    sx: { 
                        backgroundColor: 'rgba(15, 23, 24, 0.98)', 
                        backdropFilter: 'blur(12px)',
                        boxShadow: 'none',
                        borderRadius: '28px',
                        overflow: 'hidden',
                        m: 2,
                        height: '80vh',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                    }
                }}
            >
                <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', p: 1 }}>
                    {/* Viewer Header */}
                    <Box sx={{ 
                        width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', 
                        p: 2, position: 'absolute', top: 0, left: 0, zIndex: 10 
                    }}>
                        <Box sx={{ display: 'flex', gap: 1, backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '12px', p: 0.5 }}>
                            <Tooltip title="Zoom In">
                                <IconButton onClick={() => setZoom(prev => Math.min(prev + 0.5, 4))} sx={{ color: '#fff' }}>
                                    <IconZoomIn size={22} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Zoom Out">
                                <IconButton onClick={() => setZoom(prev => Math.max(prev - 0.5, 0.5))} sx={{ color: '#fff' }}>
                                    <IconZoomOut size={22} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Reset">
                                <IconButton onClick={() => setZoom(1)} sx={{ color: '#fff' }}>
                                    <IconRefresh size={22} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <IconButton onClick={() => setViewerOpen(false)} sx={{ backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' } }}>
                            <IconX size={24} />
                        </IconButton>
                    </Box>

                    {/* Image Container */}
                    <Box sx={{ 
                        width: '100%', height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', 
                        overflow: 'auto', p: 4, mt: 6,
                        '&::-webkit-scrollbar': { display: 'none' }
                    }}>
                        <img 
                            src={viewerImage} 
                            alt="Full crop" 
                            style={{ 
                                maxWidth: '100%', 
                                maxHeight: '100%', 
                                objectFit: 'contain',
                                transform: `scale(${zoom})`,
                                transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                borderRadius: '8px'
                            }} 
                        />
                    </Box>
                </Box>
            </Dialog>
        </Dialog>
    );
};

export default CropDetailsModal;
